import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@roger/ui-components';
import { ArrowLeft, Maximize2, Minimize2, Pencil, X } from 'lucide-react';
import { useStore } from '../../store';
import { NEW_MESSAGE_WINDOW } from '../../store/slices/chatWindows';
import { chatKeys, useUnreadTotal } from '../../hooks/chat/useChatQueries';
import { useFocusTrap } from '../../hooks/chat/useFocusTrap';
import { useKeyboardInset } from '../../hooks/chat/useKeyboardInset';
import { useMobileBackClose } from '../../hooks/chat/useMobileBackClose';
import { useOpenChatFromChart } from '../../hooks/chat/useOpenChatFromChart';
import { ChatGlyph } from './ChatGlyph';
import { UnreadPill } from './UnreadPill';
import { ChatInboxList } from './ChatInboxList';
import { ChannelMenu } from './ChannelMenu';
import { MOTION } from './chatMotion';
import { ConversationHeaderText } from './ConversationHeaderText';
import { PatientBand } from './PatientBand';
import { ConnectionStrip } from './ConnectionStrip';
import { ManageGroupsMenu } from './ManageGroupsMenu';
import { WindowBody } from './WindowBody';
import { useConversationHeader } from './windowHeader';

/** A drag on the grabber of at least this many px closes (down) or enlarges (up). */
const DRAG_PX = 80;

/**
 * Below 1024 px, chat is one bottom sheet showing the inbox or one
 * conversation. Closed, it is a "Messages" pill. Tablets (768 to 1023 px) get
 * the same sheet at 480 px wide, anchored right.
 */
export function ChatSheet() {
  const open = useStore(state => state.chatSheetOpen);
  const unread = useUnreadTotal();
  const openFromChart = useOpenChatFromChart();
  useMobileBackClose();
  const pillRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(open);
  useEffect(() => {
    // A closed sheet hands focus back to the pill that opened it.
    if (wasOpen.current && !open) pillRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  if (open) return <SheetDialog />;
  return (
    <button
      ref={pillRef}
      type='button'
      data-chat-sheet='pill'
      data-chat-surface=''
      aria-label={unread ? `Open messages, ${unread} unread` : 'Open messages'}
      aria-expanded={false}
      onClick={openFromChart}
      className='fixed bottom-6 right-4 z-40 mb-[env(safe-area-inset-bottom)] flex h-12 items-center gap-2 rounded-full border bg-background px-4 hover:bg-muted'
    >
      <ChatGlyph className='h-5 w-5 text-primary-600' />
      <span className='text-sm font-semibold'>Messages</span>
      <UnreadPill count={unread} />
    </button>
  );
}

function SheetDialog() {
  const open = useStore(state => state.chatWindows.open);
  const view = useStore(state => state.chatSheetView);
  const setChatSheetOpen = useStore(state => state.setChatSheetOpen);
  const setChatSheetView = useStore(state => state.setChatSheetView);
  const openChatWindow = useStore(state => state.openChatWindow);
  const setChatWindowView = useStore(state => state.setChatWindowView);
  const closeChatWindow = useStore(state => state.closeChatWindow);
  const queryClient = useQueryClient();
  const unread = useUnreadTotal();
  const [fullScreen, setFullScreen] = useState(false);
  // How far the keyboard covers the layout viewport.
  const viewport = useKeyboardInset();
  const dragStartY = useRef<number | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // The sheet is modal: Tab and Shift+Tab wrap at its ends. A menu or the
  // lightbox draws outside or over it and keeps its own Tab.
  const onKeyDown = useFocusTrap(dialogRef);

  const id = open[0];
  const conversation = view === 'conversation' && id !== undefined;
  const isNew = id === NEW_MESSAGE_WINDOW;
  const { header, card, title, subtitle } = useConversationHeader(
    id,
    conversation
  );
  const windowView = useStore(state =>
    id ? (state.windowViews[id] ?? 'conversation') : 'conversation'
  );

  // Focus moves into the sheet when it opens and whenever the control that had
  // it goes with a view change. It lands on the sheet itself, never the search
  // field or the composer: the phone keyboard must not cover the sheet.
  useEffect(() => {
    const dialog = dialogRef.current;
    const active = document.activeElement;
    if (!dialog || (dialog.contains(active) && active !== dialog)) return;
    dialog.focus();
  }, [conversation, windowView, id]);

  useEffect(() => {
    // The inbox is refetched on open: the 30 s staleTime would otherwise serve
    // the rows fetched when the tray mounted.
    void queryClient.invalidateQueries({ queryKey: chatKeys.inbox() });
  }, [queryClient]);

  // System Back and gone conversations change the view without the in-sheet
  // Back control: full screen belongs to a conversation.
  useEffect(() => {
    if (!conversation) setFullScreen(false);
  }, [conversation]);

  const close = () => setChatSheetOpen(false);
  const back = () => setChatSheetView('inbox');
  const newMessage = () => openChatWindow(NEW_MESSAGE_WINDOW);

  const onGrabberDown = (event: PointerEvent<HTMLDivElement>) => {
    dragStartY.current = event.clientY;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onGrabberUp = (event: PointerEvent<HTMLDivElement>) => {
    const start = dragStartY.current;
    dragStartY.current = null;
    if (start === null) return;
    const dy = event.clientY - start;
    if (dy >= DRAG_PX) close();
    else if (dy <= -DRAG_PX && conversation) setFullScreen(true);
  };

  // `dvh` ignores the on-screen keyboard, so every height also caps at the
  // visual viewport (`--chat-vvh`, set from useKeyboardInset).
  const full = fullScreen && conversation;
  const height = full
    ? 'h-[min(100dvh,var(--chat-vvh,100dvh))] rounded-none'
    : conversation
      ? 'h-[min(720px,calc(100dvh-24px),var(--chat-vvh,100dvh))] rounded-t-2xl'
      : 'h-[min(640px,calc(100dvh-48px),var(--chat-vvh,100dvh))] rounded-t-2xl';

  return (
    <>
      <div
        aria-hidden='true'
        data-testid='chat-sheet-scrim'
        onClick={close}
        className={`fixed inset-0 z-40 bg-foreground/40 ${MOTION.fade}`}
      />
      <div
        ref={dialogRef}
        role='dialog'
        aria-modal='true'
        aria-label='Messages'
        data-chat-sheet='dialog'
        data-chat-surface=''
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={
          {
            bottom: viewport.bottom,
            ...(viewport.height
              ? { '--chat-vvh': `${viewport.height}px` }
              : {}),
          } as CSSProperties
        }
        // A file dropped on the header must not make the browser open it; the
        // conversation body handles drops meant for the composer.
        onDragOver={event => event.preventDefault()}
        onDrop={event => event.preventDefault()}
        className={`fixed inset-x-0 z-50 flex flex-col overflow-hidden border bg-background outline-none md:left-auto md:right-0 md:w-full md:max-w-[480px] ${height} ${MOTION.sheet}`}
      >
        {!full && (
          <div
            data-testid='chat-sheet-grabber'
            onPointerDown={onGrabberDown}
            onPointerUp={onGrabberUp}
            onPointerCancel={() => (dragStartY.current = null)}
            className='flex h-11 shrink-0 touch-none items-center justify-center'
          >
            <span className='h-1 w-10 rounded-full bg-muted-foreground/40' />
          </div>
        )}
        {conversation ? (
          <div className='flex h-[52px] shrink-0 items-center gap-2 border-b pl-2 pr-2'>
            {/* Members and Announcers carry their own Back. */}
            {windowView === 'conversation' && (
              <Button
                size='iconLg'
                variant='ghost'
                aria-label='Back to messages'
                title='Back to messages'
                onClick={back}
              >
                <ArrowLeft className='h-3.5 w-3.5' />
              </Button>
            )}
            <ConversationHeaderText
              card={card}
              title={title}
              subtitle={subtitle}
            />
            {header && !isNew && (
              <ChannelMenu
                touch
                header={header}
                onShowMembers={() => setChatWindowView(id, 'members')}
                onShowAnnouncers={() => setChatWindowView(id, 'announcers')}
              />
            )}
            <Button
              size='iconLg'
              variant='ghost'
              aria-label={full ? 'Exit full screen' : 'Open full screen'}
              title={full ? 'Exit full screen' : 'Open full screen'}
              onClick={() => setFullScreen(value => !value)}
            >
              {full ? (
                <Minimize2 className='h-3.5 w-3.5' />
              ) : (
                <Maximize2 className='h-3.5 w-3.5' />
              )}
            </Button>
            <CloseButton onClick={close} />
          </div>
        ) : (
          <div className='flex h-[52px] shrink-0 items-center gap-1 border-b pl-3 pr-2'>
            <h2 className='text-sm font-semibold'>Messages</h2>
            <UnreadPill count={unread} />
            <div className='ml-auto flex items-center gap-1'>
              <Button
                size='iconLg'
                variant='ghost'
                aria-label='New message'
                title='New message'
                onClick={newMessage}
              >
                <Pencil className='h-3.5 w-3.5' />
              </Button>
              <ManageGroupsMenu size='iconLg' onNavigate={close} />
              <CloseButton onClick={close} />
            </div>
          </div>
        )}

        {conversation &&
          header &&
          card &&
          header.patient_id &&
          header.episode_id && (
            <PatientBand
              card={card}
              patientId={header.patient_id}
              episodeId={header.episode_id}
              channelId={id}
              variant='phone'
              memberCount={header.member_count}
              onShowMembers={() => setChatWindowView(id, 'members')}
            />
          )}

        {!conversation ? (
          <>
            <ConnectionStrip />
            <ChatInboxList
              density='default'
              onOpen={openChatWindow}
              onNewMessage={newMessage}
            />
          </>
        ) : (
          <WindowBody
            id={id}
            touch
            onGone={() => {
              closeChatWindow(id);
              back();
            }}
          />
        )}
      </div>
    </>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      size='iconLg'
      variant='ghost'
      aria-label='Close'
      title='Close'
      onClick={onClick}
    >
      <X className='h-3.5 w-3.5' />
    </Button>
  );
}
